// The typed payloads of the five CLOSE-THE-LOOP kinds (plan 102 slice 07, #7553): every
// issue and every pull request the platform touches reaches an END, and each end the
// platform writes on GitHub is one row. The writers are the worker's (babysit, the
// orphaned-ask sweep, the diagnosis narration); the kinds and their shapes are here so a
// writer and the Terminal renderer read ONE contract. Stored vocabulary: a field is never
// renamed once a row carries it.
//
// Every row is `visibleToMaintainer: true`: each one is an outward write on the
// maintainer's own repository (a closed issue, a closed PR, a posted comment), and audit
// is trust. Every row's `input` names the account and the GitHub object; its `output` names
// what was decided and why.

import { z } from 'zod';

const RepoFullName = z.string().regex(/^[^/\s]+\/[^/\s]+$/);
const GithubNumber = z.number().int().positive();

/**
 * `issue.closed_final` — the platform posted its ONE final, disclosed answer on an issue and
 * closed it `not planned`. `reason` (closed vocabulary; a member is added only with its
 * writer):
 *
 *   - `generations_exhausted` — the orphaned-ask sweep refiled the issue's ask up to its
 *     generation bound and every generation's work failed (the bound's named failure: a
 *     deterministic failure retried forever). `generation` is the last one filed.
 *
 * `failureKinds` are the `tasks.failure_kind` values the comment named; `commentId` is the posted comment's GitHub id, null when the post
 * was not confirmed before the close.
 */
export const IssueClosedFinalInputSchema = z.object({
  accountId: z.string().min(1),
  repo: RepoFullName,
  issueNumber: GithubNumber,
  rawTaskId: z.string().min(1),
});
export const IssueClosedFinalReasonSchema = z.enum(['generations_exhausted']);
export const IssueClosedFinalOutputSchema = z.object({
  reason: IssueClosedFinalReasonSchema,
  generation: z.number().int().positive(),
  failureKinds: z.array(z.string()),
  commentId: z.number().int().positive().nullable(),
});
export type IssueClosedFinalInput = z.infer<typeof IssueClosedFinalInputSchema>;
export type IssueClosedFinalOutput = z.infer<typeof IssueClosedFinalOutputSchema>;

/**
 * `issue.diagnosis_posted` — a diagnose run ended with a REPORT instead of a pull request,
 * and the platform posted the diagnosis itself (bounded, disclosed) on the issue (#7542),
 * so the issue does not end on "the report is on the run timeline". `followUp` is what
 * the issue got next: `fix_filed` (an ask for the fix, `fixRawTaskId`) or `none`.
 * Closed vocabulary; a member is added only with its writer. `truncated` is true
 * when the report was cut to the comment bound.
 */
export const IssueDiagnosisPostedInputSchema = z.object({
  accountId: z.string().min(1),
  repo: RepoFullName,
  issueNumber: GithubNumber,
  taskId: z.string().min(1),
});
export const IssueDiagnosisFollowUpSchema = z.enum(['fix_filed', 'none']);
export const IssueDiagnosisPostedOutputSchema = z.object({
  followUp: IssueDiagnosisFollowUpSchema,
  fixRawTaskId: z.string().min(1).nullable(),
  commentId: z.number().int().positive().nullable(),
  truncated: z.boolean(),
});
export type IssueDiagnosisPostedInput = z.infer<typeof IssueDiagnosisPostedInputSchema>;
export type IssueDiagnosisPostedOutput = z.infer<typeof IssueDiagnosisPostedOutputSchema>;

/**
 * `issue.escalation_posted` — an issue ask settled `escalated`, and the platform posted the
 * escalation's outcome on the issue ONCE (#7538, slice 07a review F4), so the issue's last
 * word is no longer "a maintainer will review it". `reason` is the verdict's own
 * `reason` (free text, as triage wrote it; null when the verdict carried none);
 * `commentId` is the posted comment's GitHub id, null when the post was not confirmed.
 */
export const IssueEscalationPostedInputSchema = z.object({
  accountId: z.string().min(1),
  repo: RepoFullName,
  issueNumber: GithubNumber,
  rawTaskId: z.string().min(1),
});
export const IssueEscalationPostedOutputSchema = z.object({
  reason: z.string().nullable(),
  commentId: z.number().int().positive().nullable(),
});
export type IssueEscalationPostedInput = z.infer<typeof IssueEscalationPostedInputSchema>;
export type IssueEscalationPostedOutput = z.infer<typeof IssueEscalationPostedOutputSchema>;

/**
 * `pr.self_refiled` — babysit closed OUR OWN pull request, refused on gate paths it also
 * touched beside other work, with a disclosed explanation, and refiled the non-gate part
 * as a new ask carrying the constraint "gate paths untouched: <list>" (07 row c). Once per
 * lineage (standing-close's bound). `gatePaths` are the paths the refile must not touch;
 * `refiledRawTaskId` / `refiledDedupeKey` name the ask filed (null id when the filing
 * landed on an existing row the key already held).
 */
export const PrSelfRefiledInputSchema = z.object({
  accountId: z.string().min(1),
  repo: RepoFullName,
  prNumber: GithubNumber,
  taskId: z.string().min(1),
});
export const PrSelfRefiledOutputSchema = z.object({
  refusalCode: z.string().min(1),
  gatePaths: z.array(z.string().min(1)).min(1),
  refiledRawTaskId: z.string().min(1).nullable(),
  refiledDedupeKey: z.string().min(1),
});
export type PrSelfRefiledInput = z.infer<typeof PrSelfRefiledInputSchema>;
export type PrSelfRefiledOutput = z.infer<typeof PrSelfRefiledOutputSchema>;

/**
 * `pr.superseded_closed` — babysit closed OUR OWN pull request that a newer state of the
 * default branch made moot, with a disclosed reason (#7536). `reason` is closed vocabulary:
 * `main_recovered` — a red-main repair PR whose default branch went green through another
 * change; `recoveredSha` is the default-branch commit whose CI was green.
 */
export const PrSupersededClosedInputSchema = z.object({
  accountId: z.string().min(1),
  repo: RepoFullName,
  prNumber: GithubNumber,
  taskId: z.string().min(1).nullable(),
});
export const PrSupersededReasonSchema = z.enum(['main_recovered']);
export const PrSupersededClosedOutputSchema = z.object({
  reason: PrSupersededReasonSchema,
  recoveredSha: z.string().regex(/^[0-9a-f]{40}$/),
});
/**
 * `external_intake.graded` — babysit graded a PR whose author the trust line calls `external`,
 * on its diff alone (#7785, `apps/worker` `babysit/external-intake.ts`). Written before a close.
 * NO AUTHOR TEXT, on purpose: rule, decision and counts, never a login or a path, because a file
 * name is the author's words. `decision` is what the grade meant for this PR: `close`, `pass`,
 * `hold` (unreadable: no merge, no close), `human_lane` (a repo a person keeps) or `reopened`.
 */
export const ExternalIntakeGradedInputSchema = z.object({
  accountId: z.string().min(1),
  repo: RepoFullName,
  prNumber: GithubNumber,
  taskId: z.string().min(1),
});
export const ExternalIntakeGradedOutputSchema = z.object({
  headSha: z.string().min(1),
  rule: z.enum([
    'none',
    'unreadable',
    'dependency',
    'ci',
    'release',
    'instruction',
    'sweeping',
    'strict_review',
  ]),
  decision: z.enum(['close', 'pass', 'hold', 'human_lane', 'reopened']),
  matched: z.number().int().nonnegative(),
  files: z.number().int().nonnegative(),
  lines: z.number().int().nonnegative(),
  topDirs: z.number().int().nonnegative(),
});
export type ExternalIntakeGradedInput = z.infer<typeof ExternalIntakeGradedInputSchema>;
export type ExternalIntakeGradedOutput = z.infer<typeof ExternalIntakeGradedOutputSchema>;
export type PrSupersededClosedInput = z.infer<typeof PrSupersededClosedInputSchema>;
export type PrSupersededClosedOutput = z.infer<typeof PrSupersededClosedOutputSchema>;

const GitSha = z.string().regex(/^[0-9a-f]{40}$/);

/**
 * `branch.deleted` — the platform deleted a branch on the maintainer's repository (07 row d,
 * slice 07b). Written AFTER the DELETE landed, one row per ref. `trigger` (closed vocabulary;
 * a member is added only with its writer):
 *
 *   - `pr_closed` — the `pull_request.closed` projection: the PR's head, right after it ended;
 *   - `janitor_pr` — the weekly branch janitor: the head of a PR that ended past its window;
 *   - `janitor_task` — the weekly branch janitor: our own `dz/` branch whose task ended
 *     without a pull request.
 *
 * `headSha` is the tip the guards read before the DELETE. `prNumber` names the PR whose head
 * it was (null for `janitor_task`); `taskId` the task whose branch it was (null when none).
 */
export const BranchDeletedInputSchema = z.object({
  accountId: z.string().min(1),
  repo: RepoFullName,
  branch: z.string().min(1),
  headSha: GitSha,
});
export const BranchDeletedTriggerSchema = z.enum(['pr_closed', 'janitor_pr', 'janitor_task']);
export const BranchDeletedOutputSchema = z.object({
  trigger: BranchDeletedTriggerSchema,
  prNumber: GithubNumber.nullable(),
  taskId: z.string().min(1).nullable(),
});
export type BranchDeletedInput = z.infer<typeof BranchDeletedInputSchema>;
export type BranchDeletedOutput = z.infer<typeof BranchDeletedOutputSchema>;
